export const COMMERCIAL_STORAGE_KEYS = Object.freeze({
  ORDER_ID: "lb.commercial.orderId",
  ACCESS_TOKEN: "lb.commercial.accessToken",
  CHECKOUT_URL: "lb.commercial.checkoutUrl",
  IDEMPOTENCY_KEY: "lb.commercial.idempotencyKey",
});

export function readCommercialOrderId(storage = window.localStorage) {
  try {
    const value = storage.getItem(COMMERCIAL_STORAGE_KEYS.ORDER_ID);
    return typeof value === "string" && value ? value : null;
  } catch (_) {
    return null;
  }
}

export function readCommercialAccessToken(storage = window.localStorage) {
  try {
    const value = storage.getItem(COMMERCIAL_STORAGE_KEYS.ACCESS_TOKEN);
    return typeof value === "string" && value ? value : null;
  } catch (_) {
    return null;
  }
}

export function persistCommercialCheckout(
  { orderId, accessToken, checkoutUrl },
  storage = window.localStorage,
) {
  storage.setItem(COMMERCIAL_STORAGE_KEYS.ORDER_ID, orderId);
  storage.setItem(COMMERCIAL_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  if (checkoutUrl) storage.setItem(COMMERCIAL_STORAGE_KEYS.CHECKOUT_URL, checkoutUrl);
  else storage.removeItem(COMMERCIAL_STORAGE_KEYS.CHECKOUT_URL);
}

export function clearCommercialSession(storage = window.localStorage) {
  try {
    storage.removeItem(COMMERCIAL_STORAGE_KEYS.ORDER_ID);
    storage.removeItem(COMMERCIAL_STORAGE_KEYS.ACCESS_TOKEN);
    storage.removeItem(COMMERCIAL_STORAGE_KEYS.CHECKOUT_URL);
  } catch (_) {
    // Authority cleanup is best-effort. Preserve checkout intent and report/input keys.
  }
}

export function readCommercialIdempotencyKey(storage = window.localStorage) {
  try {
    const value = storage.getItem(COMMERCIAL_STORAGE_KEYS.IDEMPOTENCY_KEY);
    return typeof value === "string" && value.length >= 8 && value.length <= 128
      ? value
      : null;
  } catch (_) {
    return null;
  }
}

export function persistCommercialIdempotencyKey(
  idempotencyKey,
  storage = window.localStorage,
) {
  if (
    typeof idempotencyKey !== "string" ||
    idempotencyKey.length < 8 ||
    idempotencyKey.length > 128
  ) {
    return false;
  }

  try {
    storage.setItem(COMMERCIAL_STORAGE_KEYS.IDEMPOTENCY_KEY, idempotencyKey);
    return storage.getItem(COMMERCIAL_STORAGE_KEYS.IDEMPOTENCY_KEY) === idempotencyKey;
  } catch (_) {
    return false;
  }
}

export function clearCommercialCheckoutIntent(storage = window.localStorage) {
  try {
    storage.removeItem(COMMERCIAL_STORAGE_KEYS.IDEMPOTENCY_KEY);
  } catch (_) {
    // Best-effort cleanup. A new checkout must still persist a fresh key before POST.
  }
}

export function readCommercialCheckoutUrl(storage = window.localStorage) {
  try {
    const value = storage.getItem(COMMERCIAL_STORAGE_KEYS.CHECKOUT_URL);
    return typeof value === "string" && value ? value : null;
  } catch (_) {
    return null;
  }
}

export function newCheckoutIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `web-${crypto.randomUUID()}`;
  }
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getOrCreateCommercialIdempotencyKey(storage = window.localStorage) {
  const existing = readCommercialIdempotencyKey(storage);
  if (existing) return existing;

  const created = newCheckoutIdempotencyKey();
  return persistCommercialIdempotencyKey(created, storage) ? created : null;
}

export function startNewCommercialCheckoutIntent(storage = window.localStorage) {
  clearCommercialCheckoutIntent(storage);
  return getOrCreateCommercialIdempotencyKey(storage);
}

export async function createCommercialOrder({ name, email, idempotencyKey }) {
  const response = await fetch("/api/commercial/orders", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({
      product_code: "LIFE_BLUEPRINT_REPORT",
      customer: { name, email },
    }),
  });

  let body = null;
  try { body = await response.json(); } catch (_) { body = null; }

  if (!response.ok) {
    const error = new Error((body && body.detail) || "checkout unavailable");
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

export async function getCommercialOrderStatus(orderId, accessToken) {
  const response = await fetch(
    `/api/commercial/orders/${encodeURIComponent(orderId)}/status`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  let body = null;
  try { body = await response.json(); } catch (_) { body = null; }

  if (!response.ok) {
    const error = new Error((body && body.detail) || "order status unavailable");
    error.status = response.status;
    throw error;
  }
  return body;
}

export function hasPaidEntitlement(status) {
  return !!status &&
    status.status === "PAID" &&
    status.entitlement_status === "ACTIVE";
}

export function hasPaidReportAccess(status) {
  return !!status &&
    status.status === "PAID" &&
    (status.entitlement_status === "ACTIVE" ||
      status.entitlement_status === "CONSUMED");
}

async function commercialReportRequest(path, {
  method = "GET",
  accessToken,
  body,
  fallbackMessage,
} = {}) {
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${accessToken}`,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(path, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  let parsed = null;
  try { parsed = await response.json(); } catch (_) { parsed = null; }

  if (!response.ok) {
    const error = new Error((parsed && parsed.detail) || fallbackMessage || "report unavailable");
    error.status = response.status;
    error.body = parsed;
    throw error;
  }
  return parsed;
}

export function claimCommercialReport(orderId, accessToken, input) {
  return commercialReportRequest(
    `/api/commercial/orders/${encodeURIComponent(orderId)}/report`,
    {
      method: "POST",
      accessToken,
      body: input,
      fallbackMessage: "report claim unavailable",
    },
  );
}

export function generateCommercialReport(orderId, accessToken) {
  return commercialReportRequest(
    `/api/commercial/orders/${encodeURIComponent(orderId)}/report/generate`,
    {
      method: "POST",
      accessToken,
      fallbackMessage: "report generation unavailable",
    },
  );
}

export function getCommercialReport(orderId, accessToken) {
  return commercialReportRequest(
    `/api/commercial/orders/${encodeURIComponent(orderId)}/report`,
    {
      method: "GET",
      accessToken,
      fallbackMessage: "report unavailable",
    },
  );
}

export async function generateCommercialPdf(orderId, accessToken, locale = "zh") {
  return commercialReportRequest(
    `/api/commercial/orders/${encodeURIComponent(orderId)}/report/pdf?locale=${encodeURIComponent(locale)}`,
    {
      method: "POST",
      accessToken,
      fallbackMessage: "report PDF generation unavailable",
    },
  );
}

export async function getCommercialPdf(orderId, accessToken, locale = "zh") {
  const response = await fetch(
    `/api/commercial/orders/${encodeURIComponent(orderId)}/report/pdf?locale=${encodeURIComponent(locale)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/pdf",
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch (_) { body = null; }
    const error = new Error((body && body.detail) || "report PDF unavailable");
    error.status = response.status;
    error.body = body;
    throw error;
  }

  const blob = await response.blob();
  return {
    blob,
    filename: parseAttachmentFilename(response.headers.get("content-disposition")),
    reportId: response.headers.get("x-report-id"),
    artifactDigest: response.headers.get("x-pdf-sha256"),
    contentType: response.headers.get("content-type"),
    locale: response.headers.get("x-pdf-locale"),
  };
}

function parseAttachmentFilename(value) {
  if (typeof value !== "string") return "Life_Blueprint.pdf";
  const match = value.match(/filename="([^"]+)"/i);
  return match && match[1] ? match[1] : "Life_Blueprint.pdf";
}

export function validateCommercialPdfMetadata(value) {
  const valid = !!value &&
    value.status === "READY" &&
    typeof value.report_id === "string" &&
    value.report_id.startsWith("rpt_") &&
    typeof value.source_snapshot_digest === "string" &&
    value.source_snapshot_digest.length === 64 &&
    typeof value.artifact_digest === "string" &&
    value.artifact_digest.length === 64 &&
    Number.isInteger(value.size_bytes) &&
    value.size_bytes > 0 &&
    typeof value.renderer_version === "string" &&
    value.renderer_version.length > 0 &&
    (value.locale === "zh" || value.locale === "en");

  return valid
    ? { ok: true, metadata: value }
    : { ok: false, reason: "INVALID_CONTRACT" };
}

const COMMERCIAL_REPORT_SECTIONS = Object.freeze([
  "profile", "businessContext", "core", "indices", "career", "strategy",
  "annualAdvice", "directionAdvice", "numberAdvice", "actionPlan90Day",
  "wealthBlueprint",
]);

export function validateCommercialReportResponse(value) {
  const report = value && value.report;
  const sectionsValid = report !== null &&
    typeof report === "object" &&
    !Array.isArray(report) &&
    COMMERCIAL_REPORT_SECTIONS.every((key) => {
      const section = report[key];
      return section !== null && typeof section === "object" && !Array.isArray(section);
    });
  const valid = !!value &&
    value.status === "READY" &&
    value.engine_version === "V2.9.9" &&
    typeof value.snapshot_digest === "string" &&
    value.snapshot_digest.length === 64 &&
    sectionsValid &&
    report.source === "VALIDATED_V299" &&
    report.engineVersion === "V2.9.9" &&
    report.provenance &&
    report.provenance.module === "PRODUCTION_REPORT_ADAPTER";

  return valid
    ? { ok: true, report, metadata: value }
    : { ok: false, reason: "INVALID_CONTRACT" };
}
